/**
 * Error handling and logging utilities
 */

import { Environment } from '../environment';
import { isDefined, isNullOrUndefined, hasProperty } from './common';
import { NETWORK_ERROR_CODES, NETWORK_ERROR_MESSAGE_PATTERNS } from '../constants';
import { ErrorWithCode } from '../interfaces';

// Error types for better categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

// Log levels
export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug'
}

// Structured error interface
export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: unknown;
  statusCode?: number;
  context?: Record<string, unknown>;
  timestamp: Date;
}

// API Error response structure
interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string | string[];
      error?: string;
      statusCode?: number;
      metadata?: {
        errors?: Array<{
          field?: string;
          message?: string;
        }>;
      };
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
  name?: string;
}

/**
 * Logger utility for consistent logging across the app
 */
export class Logger {
  private static isDevelopment = process.env.NODE_ENV === Environment.DEVELOPMENT;

  static log(level: LogLevel, message: string, data?: unknown, context?: string): void {
    if (!this.isDevelopment && level === LogLevel.DEBUG) {
      return; // Don't log debug in production
    }

    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    const logMessage = `${timestamp} ${contextStr} ${message}`;

    switch (level) {
      case LogLevel.ERROR:
        console.error(logMessage, data);
        break;
      case LogLevel.WARN:
        console.warn(logMessage, data);
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(logMessage, data);
        break;
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console
        console.debug(logMessage, data);
        break;
    }
  }

  static error(message: string, error?: unknown, context?: string): void {
    this.log(LogLevel.ERROR, message, error, context);
  }

  static warn(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  static info(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  static debug(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }
}

/**
 * Type guard for API error responses
 */
function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return typeof error === 'object' && error !== null && 'response' in error;
}

/**
 * Collapse an API `message` field to a single displayable string.
 *
 * @remarks
 * NestJS's `ValidationPipe` returns `message` as an array of per-field failures. Treating that
 * array as a string drops every entry after the first once it reaches a toast, so join them.
 */
function normaliseApiMessage(message: unknown): string | undefined {
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    const parts = message.filter((entry): entry is string => typeof entry === 'string');
    return parts.length > 0 ? parts.join('. ') : undefined;
  }
  return undefined;
}

/**
 * Parse and categorize errors from API responses
 */
export function parseApiError(error: unknown): AppError {
  const timestamp = new Date();

  // Handle null/undefined errors
  if (isNullOrUndefined(error)) {
    return {
      type: ErrorType.UNKNOWN,
      message: 'An unknown error occurred',
      originalError: error,
      timestamp
    };
  }

  // Handle API response errors (axios, fetch, etc.)
  if (isApiErrorResponse(error)) {
    const apiError = error;
    const response = apiError.response;
    const statusCode = response?.status;
    const data = response?.data;
    
    // Extract error message - prioritize metadata.errors if available
    let errorMessage = normaliseApiMessage(data?.message) ?? data?.error ?? response?.statusText;
    
    // Check for metadata.errors array and combine messages
    if (data?.metadata?.errors && Array.isArray(data.metadata.errors) && data.metadata.errors.length > 0) {
      const errorMessages: string[] = [];
      for (const err of data.metadata.errors) {
        if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
          // Keep the field name: "Please check your input" is useless without knowing which input.
          const field = "field" in err && typeof err.field === "string" ? err.field : undefined;
          errorMessages.push(isDefined(field) ? `${field}: ${err.message}` : err.message);
        }
      }
      if (errorMessages.length > 0) {
        errorMessage = errorMessages.join(". ");
      }
    }

    let errorType = ErrorType.SERVER;
    if (statusCode) {
      if (statusCode === 401) errorType = ErrorType.AUTHENTICATION;
      else if (statusCode === 403) errorType = ErrorType.AUTHORIZATION;
      else if (statusCode >= 400 && statusCode < 500) errorType = ErrorType.CLIENT;
      else if (statusCode >= 500) errorType = ErrorType.SERVER;
    }

    return {
      type: errorType,
      message: errorMessage ?? 'Server error occurred',
      originalError: error,
      statusCode,
      timestamp,
      context: {
        url: response?.data,
        status: statusCode,
        statusText: response?.statusText
      }
    };
  }

  // Handle JavaScript Error objects
  if (error instanceof Error) {
    let errorType = ErrorType.CLIENT;
    
    // Network errors
    if (error.name === 'NetworkError' || error.message.includes('network')) {
      errorType = ErrorType.NETWORK;
    }
    // Validation errors
    else if (error.name === 'ValidationError' || error.message.includes('validation')) {
      errorType = ErrorType.VALIDATION;
    }

    return {
      type: errorType,
      message: error.message,
      originalError: error,
      timestamp,
      context: {
        name: error.name,
        stack: error.stack
      }
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      type: ErrorType.UNKNOWN,
      message: error,
      originalError: error,
      timestamp
    };
  }

  // Fallback for unknown error types
  return {
    type: ErrorType.UNKNOWN,
    message: 'An unexpected error occurred',
    originalError: error,
    timestamp,
    context: {
      errorType: typeof error,
      errorValue: String(error)
    }
  };
}

/**
 * Get user-friendly error message for display
 */
export function getUserFriendlyMessage(error: AppError, defaultMessage?: string): string {
  const fallback = defaultMessage ?? 'Something went wrong. Please try again.';

  switch (error.type) {
    case ErrorType.NETWORK:
      return 'Network connection error. Please check your internet connection and try again.';
    
    case ErrorType.AUTHENTICATION:
      return 'Authentication failed. Please log in again.';
    
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    
    case ErrorType.VALIDATION:
      return error.message ?? 'Please check your input and try again.';
    
    case ErrorType.SERVER:
      if (error.statusCode === 500) {
        return 'Server error occurred. Please try again later.';
      }
      return error.message ?? fallback;
    
    case ErrorType.CLIENT:
      // Handle specific client errors with better messages
      if (error.statusCode === 400) {
        // Check for specific error messages from API
        if (error.message?.includes('discount code') || error.message?.includes('discount')) {
          return error.message; // Use the specific discount error message from API
        }
        if (error.message?.includes('invalid') || error.message?.includes('not valid')) {
          return error.message; // Use specific validation error messages
        }
        return error.message ?? 'Invalid request. Please check your input and try again.';
      }
      return error.message ?? 'Invalid request. Please check your input.';
    
    default:
      return error.message ?? fallback;
  }
}

/**
 * Main error handler that logs and returns user-friendly message
 */
export function handleError(
  error: unknown, 
  context?: string, 
  defaultMessage?: string
): string {
  const parsedError = parseApiError(error);
  
  // Log the error with context
  Logger.error(
    `Error in ${context ?? 'unknown context'}`, 
    {
      type: parsedError.type,
      message: parsedError.message,
      statusCode: parsedError.statusCode,
      context: parsedError.context,
      originalError: parsedError.originalError
    },
    context
  );

  // Return user-friendly message
  return getUserFriendlyMessage(parsedError, defaultMessage);
}

/**
 * Async error wrapper for promises
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context?: string,
  defaultErrorMessage?: string
): Promise<{ data?: T; error?: string }> {
  try {
    const data = await operation();
    return { data };
  } catch (error) {
    const errorMessage = handleError(error, context, defaultErrorMessage);
    return { error: errorMessage };
  }
}

/**
 * React error boundary helper
 */
export function getErrorBoundaryMessage(error: Error, errorInfo?: { componentStack: string }): string {
  Logger.error('React Error Boundary caught an error', {
    error: error.message,
    stack: error.stack,
    componentStack: errorInfo?.componentStack
  }, 'ErrorBoundary');

  return 'Something went wrong. Please refresh the page and try again.';
}

/**
 * Specific error handler for order-related operations
 */
export function handleOrderError(error: unknown, context?: string): string {
  const parsedError = parseApiError(error);
  
  // Log the error with order context
  Logger.error(
    `Order error in ${context ?? 'unknown context'}`, 
    {
      type: parsedError.type,
      message: parsedError.message,
      statusCode: parsedError.statusCode,
      context: parsedError.context,
      originalError: parsedError.originalError
    },
    'OrderError'
  );

  // Handle specific order-related errors
  if (parsedError.statusCode === 400) {
    // Bad request - likely validation error
    if (parsedError.message?.includes('discount code')) {
      return parsedError.message; // "This discount code is not valid. Please try a different discount code."
    }
    if (parsedError.message?.includes('insufficient coins') || parsedError.message?.includes('coins')) {
      return parsedError.message; // Specific coin-related error
    }
    if (parsedError.message?.includes('slot') || parsedError.message?.includes('time')) {
      return parsedError.message; // Slot-related errors
    }
    if (parsedError.message?.includes('product') || parsedError.message?.includes('variant')) {
      return parsedError.message; // Product-related errors
    }
    if (parsedError.message?.includes('address') || parsedError.message?.includes('location')) {
      return parsedError.message; // Address-related errors
    }
    // Generic validation error for 400
    return parsedError.message ?? 'Please check your order details and try again.';
  }

  if (parsedError.statusCode === 409) {
    return 'This order conflicts with existing data. Please check your selections and try again.';
  }

  if (parsedError.statusCode === 422) {
    return parsedError.message ?? 'Order data is invalid. Please check your selections and try again.';
  }

  // Fall back to general error handling
  return getUserFriendlyMessage(parsedError, 'Failed to process order. Please try again.');
}

function isErrorWithCode(error: unknown): error is ErrorWithCode {
  return typeof error === 'object' && error !== null && ('code' in error || 'request' in error);
}

/**
 * Safely get code property from error if it exists and is a string
 */
function getErrorCode(error: unknown): string | undefined {
  if (isErrorWithCode(error) && isDefined(error.code) && typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
}

/**
 * Detect network errors robustly without relying on error message strings
 * Handles various network error scenarios: offline, axios network failures, fetch failures, etc.
 */
export function isNetworkError(error: unknown): boolean {
  // Check if user is offline (strongest signal)
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  // Handle null/undefined
  if (isNullOrUndefined(error)) {
    return false;
  }

  // Handle Axios errors
  if (isErrorWithCode(error)) {
    // Check for Axios network error codes
    const errorCode = getErrorCode(error);
    if (isDefined(errorCode)) {
      const isNetworkCode = NETWORK_ERROR_CODES.some(code => code === errorCode);
      if (isNetworkCode) {
        return true;
      }
    }
    
    // Network failure: request was made but no response received
    // This indicates a network-level failure, not an HTTP error
    if (isDefined(error.request) && !isDefined(error.response)) {
      return true;
    }
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    // NetworkError type (from fetch API or other network operations)
    if (error.name === 'NetworkError') {
      return true;
    }
    
    // Check error message for network-related indicators
    const errorMessage = error.message.toUpperCase();
    if (NETWORK_ERROR_MESSAGE_PATTERNS.some(pattern => errorMessage.includes(pattern))) {
      return true;
    }
    
    // TypeError from fetch/network operations
    // Only consider it a network error if it doesn't have a response structure
    if (error.name === 'TypeError' && !hasProperty(error, 'response')) {
      return true;
    }
  }

  return false;
}